import { useEffect, useMemo, useState } from "react";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import { useQuery } from "@tanstack/react-query";

import CustomerPortalLayout from "components/storefront/CustomerPortalLayout";
import {
  PortalAlert,
  PortalButton,
  PortalCard,
  PortalLoading,
  PortalStatusChip,
} from "components/portal/PortalUi";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useAuth } from "context/AuthContext";
import { api, get, post } from "lib/api";
import { isCustomerRole } from "lib/portalRoles";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import usePortalTheme from "components/portal/usePortalTheme";
import {
  coreReturnLifecycleLabel,
  coreReturnWorkflowSteps,
} from "lib/coreReturnLifecycle";

const STEPS = ["confirm", "pack", "ship"];

function formatMoney(value, currency) {
  const amount = Number(value || 0);
  const code = String(currency || "USD").toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
    }).format(amount);
  } catch {
    return `${code} ${amount.toFixed(2)}`;
  }
}

function formatDateTime(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
}

function optionKey(option) {
  return [
    option?.bookingMode || "label_only",
    option?.quoteId || "",
    option?.carrier || "",
    option?.service || "",
  ].join("|");
}

function ChoiceCard({ selected, title, subtitle, onClick, disabled }) {
  const p = usePortalTheme();
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        appearance: "none",
        textAlign: "left",
        width: "100%",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        bgcolor: selected ? p.accentSoft : p.surface,
        border: `1.5px solid ${selected ? p.accent : p.lineStrong}`,
        borderRadius: `${p.radius}px`,
        p: 2,
        transition: "border-color 160ms ease, background-color 160ms ease",
        "&:hover": disabled
          ? undefined
          : { borderColor: p.accent, bgcolor: p.accentSoft },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            border: `2px solid ${selected ? p.accent : p.lineStrong}`,
            display: "grid",
            placeItems: "center",
            mt: 0.15,
            flexShrink: 0,
          }}
        >
          {selected ? (
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: p.accent,
              }}
            />
          ) : null}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <MDTypography variant="button" fontWeight={800} sx={{ color: p.ink }}>
            {title}
          </MDTypography>
          {subtitle ? (
            <MDTypography
              variant="caption"
              display="block"
              sx={{ color: p.muted, mt: 0.35, lineHeight: 1.45 }}
            >
              {subtitle}
            </MDTypography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

ChoiceCard.propTypes = {
  selected: PropTypes.bool,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
};

ChoiceCard.defaultProps = {
  selected: false,
  subtitle: null,
  onClick: undefined,
  disabled: false,
};

export default function CustomerValveBodyCoreReturn() {
  const { token, pluginRole, authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { documentId } = useParams();
  const { t } = useStorefrontCopy();
  const p = usePortalTheme();
  const role = String(pluginRole || "").toLowerCase();
  const isCustomer = isCustomerRole(role);

  const [step, setStep] = useState("confirm");
  const [notes, setNotes] = useState("");
  const [conditionChecklist, setConditionChecklist] = useState({
    isNotLeakingOil: false,
    isInPlasticBag: false,
    isPackedInOldBox: false,
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedOptionKey, setSelectedOptionKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const meQuery = useQuery({
    queryKey: ["storefront-me", "capabilities", "core-return"],
    queryFn: () => get("/api/storefront/me"),
    enabled: !!token && isCustomer,
    staleTime: 15000,
  });
  const canSubmitCore = meQuery.data?.capabilities?.submitCoreReturn !== false;
  const detailQuery = useQuery({
    queryKey: ["storefront-valve-body", documentId, "core-return"],
    queryFn: () => get(`/api/storefront/valve-bodies/${documentId}`),
    enabled: !!token && isCustomer && !!documentId,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });
  const detail = detailQuery.data?.data || null;
  const lifecycle = detail?.lifecycle || {};
  const activeRequest = lifecycle?.activeReturnRequest || null;
  const hasLabel = !!String(activeRequest?.shippingLabelUrl || "").trim();
  const providerFailed =
    String(activeRequest?.shippingProviderStatus || "")
      .trim()
      .toLowerCase() === "failed";

  const isChecklistComplete =
    conditionChecklist.isNotLeakingOil &&
    conditionChecklist.isInPlasticBag &&
    conditionChecklist.isPackedInOldBox;
  const previews = useMemo(
    () => imageFiles.map((file) => ({ file, url: URL.createObjectURL(file) })),
    [imageFiles]
  );
  const selectedOption = shippingOptions.find(
    (option) => optionKey(option) === selectedOptionKey
  );
  const stepIndex = Math.max(0, STEPS.indexOf(step));

  useEffect(
    () => () => previews.forEach((row) => URL.revokeObjectURL(row.url)),
    [previews]
  );

  useEffect(() => {
    if (!detail) return;
    if (hasLabel) {
      setStep("ship");
      return;
    }
    if (activeRequest && !hasLabel) {
      setStep("ship");
    }
  }, [detail, hasLabel, activeRequest]);

  const loadShippingOptions = async () => {
    if (!documentId) return;
    setOptionsLoading(true);
    setMessage(null);
    try {
      const res = await post(
        `/api/storefront/valve-bodies/${documentId}/core-return/shipping/options`,
        { data: { bookingMode: "label_only" } }
      );
      const options = Array.isArray(res?.data?.shipping?.options)
        ? res.data.shipping.options
        : [];
      setShippingOptions(options);
      if (options.length) {
        setSelectedOptionKey((prev) => {
          if (prev && options.some((option) => optionKey(option) === prev)) {
            return prev;
          }
          return optionKey(options[0]);
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error?.response?.data?.error?.message ||
          error?.message ||
          t(
            "coreReturn.optionsError",
            "Unable to load prepaid shipping options."
          ),
      });
    } finally {
      setOptionsLoading(false);
    }
  };

  useEffect(() => {
    if (step !== "ship" || !activeRequest || hasLabel) return;
    loadShippingOptions();
  }, [step, !!activeRequest, hasLabel, documentId]);

  if (!token) {
    return (
      <Navigate
        to="/authentication/sign-in"
        state={{ from: location }}
        replace
      />
    );
  }
  if (authLoading || pluginRole == null) return null;
  if (!isCustomer) return <Navigate to="/dashboard" replace />;
  if (!documentId) return <Navigate to="/account/core-returns" replace />;

  const onStartReturn = async () => {
    if (!canSubmitCore || !isChecklistComplete) return;
    setSaving(true);
    setMessage(null);
    try {
      let imageIds = [];
      if (imageFiles.length) {
        const formData = new FormData();
        imageFiles.forEach((file) => formData.append("files", file));
        const uploadRes = await api.post("/api/upload", formData);
        const uploadRows = Array.isArray(uploadRes?.data) ? uploadRes.data : [];
        imageIds = uploadRows
          .map((row) => Number(row?.id || 0))
          .filter((id) => Number.isFinite(id) && id > 0);
      }
      await post(`/api/storefront/valve-bodies/${documentId}/core-return`, {
        data: {
          notes,
          conditionChecklist,
          imageIds,
          bookShipping: false,
          bookingMode: "label_only",
        },
      });
      setImageFiles([]);
      await detailQuery.refetch();
      setStep("ship");
      setMessage({
        type: "success",
        text: t(
          "coreReturn.packComplete",
          "Packaging confirmed. Choose where to drop off your core."
        ),
      });
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error?.response?.data?.error?.message ||
          error?.message ||
          t("coreReturn.startError", "Failed to start core return."),
      });
    } finally {
      setSaving(false);
    }
  };

  const onPrintLabel = async () => {
    if (!selectedOption || !canSubmitCore) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await post(
        `/api/storefront/valve-bodies/${documentId}/core-return/shipping/book`,
        {
          data: {
            quoteId: selectedOption.quoteId,
            carrier: selectedOption.carrier,
            service: selectedOption.service,
            bookingMode: selectedOption.bookingMode || "label_only",
            force: providerFailed || !hasLabel,
          },
        }
      );
      const labelUrl =
        String(res?.data?.shipping?.shippingLabelUrl || "").trim() ||
        String(res?.data?.returnRequest?.shippingLabelUrl || "").trim();
      await detailQuery.refetch();
      if (labelUrl) {
        window.open(labelUrl, "_blank", "noopener,noreferrer");
      }
      setMessage({
        type: "success",
        text: t(
          "coreReturn.labelReadyMessage",
          "Your prepaid return label is ready."
        ),
      });
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error?.response?.data?.error?.message ||
          error?.message ||
          t("coreReturn.bookError", "Unable to generate the return label."),
      });
    } finally {
      setSaving(false);
    }
  };

  const unitTitle =
    detail?.partNumber ||
    detail?.displayTag ||
    detail?.serialNumber ||
    t("registeredUnits.fallbackName", "Registered Item");

  const workflowSteps = coreReturnWorkflowSteps(lifecycle, t);

  return (
    <CustomerPortalLayout
      title={t("coreReturn.title", "Return core")}
      subtitle={t(
        "coreReturn.wizardSubtitle",
        "Confirm, pack, then print your prepaid label."
      )}
      actions={
        <PortalButton
          variant="ghost"
          size="sm"
          startIcon="arrow_back"
          component={Link}
          to="/account/core-returns"
        >
          {t("common.back", "Back")}
        </PortalButton>
      }
    >
      {message ? (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          mb: 2.5,
        }}
      >
        {STEPS.map((key, index) => {
          const labels = {
            confirm: t("coreReturn.steps.confirm", "1. Confirm"),
            pack: t("coreReturn.steps.pack", "2. Pack"),
            ship: t("coreReturn.steps.ship", "3. Print label"),
          };
          const active = step === key;
          const complete = index < stepIndex || (key === "ship" && hasLabel);
          return (
            <Box
              key={key}
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: `${p.radiusSm}px`,
                bgcolor: active || complete ? p.accentSoft : p.surface,
                border: `1px solid ${
                  active || complete ? p.accentBorder : p.lineStrong
                }`,
                color: active || complete ? p.accent : p.muted,
                fontWeight: 800,
                fontSize: "0.75rem",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              {labels[key]}
            </Box>
          );
        })}
      </Box>

      {detailQuery.isLoading ? (
        <PortalLoading label={t("common.loading", "Loading...")} />
      ) : !detail ? (
        <PortalAlert severity="error">
          {t("coreReturn.notFound", "This valve body could not be loaded.")}
        </PortalAlert>
      ) : (
        <>
          <PortalCard
            accentTop
            title={unitTitle}
            subtitle={
              detail?.tag
                ? `Tag ${detail.tag}`
                : detail?.serialNumber ||
                  t("coreReturn.serialPending", "Serial pending")
            }
            action={
              <PortalStatusChip
                label={coreReturnLifecycleLabel(lifecycle, t)}
                tone="accent"
              />
            }
            sx={{ mb: 2 }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: 1.25,
              }}
            >
              <Box>
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{ opacity: 0.7 }}
                >
                  {t("coreReturn.creditLabel", "Refundable credit")}
                </MDTypography>
                <MDTypography variant="h5" fontWeight={900}>
                  {formatMoney(
                    lifecycle?.refundableCoreAmount,
                    lifecycle?.currency
                  )}
                </MDTypography>
              </Box>
              <Box>
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{ opacity: 0.7 }}
                >
                  {t("coreReturn.deadlineLabel", "Deadline")}
                </MDTypography>
                <MDTypography variant="button" fontWeight={700}>
                  {formatDateTime(lifecycle?.coreDueAt)}
                </MDTypography>
              </Box>
              <Box>
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{ opacity: 0.7 }}
                >
                  {t("coreReturn.pendingQty", "Pending qty")}
                </MDTypography>
                <MDTypography variant="button" fontWeight={700}>
                  {Number(lifecycle?.refundableCoreOutstandingQty || 0)}
                </MDTypography>
              </Box>
            </Box>
            <MDTypography
              variant="body2"
              color="text"
              sx={{ opacity: 0.8, mt: 1.25 }}
            >
              {t(
                "coreReturn.refundText",
                "Once the return is received, inspected, and approved, the eligible amount is refunded."
              )}
            </MDTypography>
          </PortalCard>

          {!canSubmitCore ? (
            <PortalAlert severity="info" sx={{ mb: 2 }}>
              {t(
                "coreReturn.membershipBlocked",
                "Your membership does not allow starting core returns."
              )}
            </PortalAlert>
          ) : null}

          {step === "confirm" ? (
            <PortalCard
              title={t("coreReturn.confirmTitle", "Confirm this core")}
              subtitle={t(
                "coreReturn.confirmSubtitle",
                "You are returning this valve body core with a prepaid VBS label."
              )}
            >
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                <PortalButton
                  variant="primary"
                  disabled={!canSubmitCore || !lifecycle?.coreDue}
                  onClick={() => setStep("pack")}
                >
                  {t("portal.returnCore", "Return core")}
                </PortalButton>
                <PortalButton
                  variant="ghost"
                  startIcon="arrow_back"
                  component={Link}
                  to="/account/core-returns"
                >
                  {t("common.back", "Back")}
                </PortalButton>
              </Box>
            </PortalCard>
          ) : null}

          {step === "pack" ? (
            <PortalCard
              title={t("coreReturn.packTitle", "Pack the core")}
              subtitle={t(
                "coreReturn.packSubtitle",
                "Follow these steps so oil does not spill in transit."
              )}
            >
              <Box
                sx={{
                  display: "grid",
                  gap: 1.25,
                  mb: 2,
                }}
              >
                {[
                  {
                    icon: "water_drop",
                    title: t(
                      "coreReturn.instructions.oilTitle",
                      "Drain all oil"
                    ),
                    body: t(
                      "coreReturn.instructions.oilBody",
                      "Drain the core completely. Wipe residue so the box is not dripping or leaking oil."
                    ),
                  },
                  {
                    icon: "shopping_bag",
                    title: t(
                      "coreReturn.instructions.bagTitle",
                      "Seal in a plastic bag"
                    ),
                    body: t(
                      "coreReturn.instructions.bagBody",
                      "Place the core in the provided plastic bag and seal it before boxing."
                    ),
                  },
                  {
                    icon: "inventory_2",
                    title: t(
                      "coreReturn.instructions.boxTitle",
                      "Use the original box"
                    ),
                    body: t(
                      "coreReturn.instructions.boxBody",
                      "Pack the bagged core in the original or old box so it ships securely."
                    ),
                  },
                ].map((item) => (
                  <Box
                    key={item.title}
                    sx={{
                      display: "flex",
                      gap: 1.25,
                      p: 1.5,
                      borderRadius: `${p.radiusSm}px`,
                      bgcolor: "rgba(15,15,18,0.03)",
                      border: `1px solid ${p.lineStrong}`,
                    }}
                  >
                    <Icon sx={{ color: p.accent, mt: 0.15 }}>{item.icon}</Icon>
                    <Box>
                      <MDTypography variant="button" fontWeight={800}>
                        {item.title}
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        color="text"
                        sx={{ opacity: 0.8, mt: 0.25 }}
                      >
                        {item.body}
                      </MDTypography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={conditionChecklist.isNotLeakingOil}
                      onChange={(event) =>
                        setConditionChecklist((prev) => ({
                          ...prev,
                          isNotLeakingOil: event.target.checked,
                        }))
                      }
                      disabled={saving}
                    />
                  }
                  label={t(
                    "coreReturn.checklist.oil",
                    "The box is not leaking or dripping oil."
                  )}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={conditionChecklist.isInPlasticBag}
                      onChange={(event) =>
                        setConditionChecklist((prev) => ({
                          ...prev,
                          isInPlasticBag: event.target.checked,
                        }))
                      }
                      disabled={saving}
                    />
                  }
                  label={t(
                    "coreReturn.sealedLabel",
                    "The item is sealed in the provided packaging."
                  )}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={conditionChecklist.isPackedInOldBox}
                      onChange={(event) =>
                        setConditionChecklist((prev) => ({
                          ...prev,
                          isPackedInOldBox: event.target.checked,
                        }))
                      }
                      disabled={saving}
                    />
                  }
                  label={t(
                    "coreReturn.checklist.box",
                    "The return uses the original or old box."
                  )}
                />
              </Box>

              {!isChecklistComplete ? (
                <PortalAlert severity="info" sx={{ mb: 2 }}>
                  {t(
                    "coreReturn.checklistRequired",
                    "Confirm all packaging items before continuing."
                  )}
                </PortalAlert>
              ) : null}

              <MDInput
                label={t("coreReturn.notes", "Notes")}
                fullWidth
                multiline
                minRows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={saving}
                sx={{ mb: 2 }}
              />

              <Box sx={{ mb: 2 }}>
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{ opacity: 0.75, display: "block", mb: 0.5 }}
                >
                  {t("coreReturn.photos", "Return photos (optional)")}
                </MDTypography>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={saving}
                  onChange={(event) =>
                    setImageFiles(Array.from(event.target.files || []))
                  }
                />
                {previews.length ? (
                  <Box
                    sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}
                  >
                    {previews.map(({ file, url }) => (
                      <Box
                        key={`${file.name}-${file.size}-${url}`}
                        component="button"
                        type="button"
                        onClick={() => setSelectedPreview(url)}
                        sx={{
                          border: "none",
                          background: "transparent",
                          padding: 0,
                          cursor: "pointer",
                        }}
                      >
                        <Box
                          component="img"
                          src={url}
                          alt={file.name}
                          sx={{
                            width: 88,
                            height: 88,
                            objectFit: "cover",
                            borderRadius: 2,
                            border: `1px solid ${p.lineStrong}`,
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                ) : null}
                {selectedPreview ? (
                  <Box
                    component="img"
                    src={selectedPreview}
                    alt="Selected return preview"
                    sx={{
                      mt: 1,
                      width: "100%",
                      maxWidth: 420,
                      borderRadius: 2,
                      border: `1px solid ${p.lineStrong}`,
                    }}
                  />
                ) : null}
              </Box>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <PortalButton
                  variant="ghost"
                  disabled={saving}
                  onClick={() => setStep("confirm")}
                >
                  {t("common.back", "Back")}
                </PortalButton>
                <PortalButton
                  variant="primary"
                  disabled={saving || !canSubmitCore || !isChecklistComplete}
                  onClick={onStartReturn}
                >
                  {saving
                    ? t("coreReturn.saving", "Saving...")
                    : t("coreReturn.continueToLabel", "Continue to label")}
                </PortalButton>
              </Box>
            </PortalCard>
          ) : null}

          {step === "ship" ? (
            <PortalCard
              title={
                hasLabel
                  ? t("coreReturn.labelReadyTitle", "Your label is ready")
                  : t("coreReturn.shipTitle", "Choose drop-off option")
              }
              subtitle={
                hasLabel
                  ? t(
                      "coreReturn.labelReadySubtitle",
                      "Print the prepaid label and drop off the packaged core."
                    )
                  : t(
                      "coreReturn.shipSubtitle",
                      "Prepaid options — no charge to you. Pick where you will take the core."
                    )
              }
            >
              {hasLabel ? (
                <>
                  <Box sx={{ mb: 2 }}>
                    <MDTypography variant="button" fontWeight={700}>
                      {[
                        activeRequest?.shippingCarrier,
                        activeRequest?.trackingNumber,
                      ]
                        .filter(Boolean)
                        .join(" • ") || "—"}
                    </MDTypography>
                  </Box>
                  <Box
                    sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}
                  >
                    <PortalButton
                      variant="ghost"
                      startIcon="arrow_back"
                      component={Link}
                      to="/account/core-returns"
                    >
                      {t("common.back", "Back")}
                    </PortalButton>
                    <PortalButton
                      variant="primary"
                      onClick={() =>
                        window.open(
                          activeRequest.shippingLabelUrl,
                          "_blank",
                          "noopener,noreferrer"
                        )
                      }
                    >
                      {t("coreReturn.printLabel", "Print label")}
                    </PortalButton>
                    {activeRequest?.shippingProviderTrackUrl ? (
                      <PortalButton
                        variant="ghost"
                        onClick={() =>
                          window.open(
                            activeRequest.shippingProviderTrackUrl,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      >
                        {t("coreReturn.trackReturn", "Track return")}
                      </PortalButton>
                    ) : null}
                    {providerFailed ? (
                      <PortalButton
                        variant="ghost"
                        disabled={saving || optionsLoading}
                        onClick={() => {
                          setShippingOptions([]);
                          loadShippingOptions();
                        }}
                      >
                        {t("coreReturn.retryOptions", "Retry shipping options")}
                      </PortalButton>
                    ) : null}
                  </Box>
                </>
              ) : (
                <>
                  {optionsLoading ? (
                    <PortalLoading
                      label={t(
                        "coreReturn.loadingOptions",
                        "Loading prepaid options..."
                      )}
                    />
                  ) : shippingOptions.length ? (
                    <Box
                      sx={{
                        display: "grid",
                        gap: 1.25,
                        mb: 2,
                      }}
                    >
                      {shippingOptions.map((option) => {
                        const key = optionKey(option);
                        const selected = selectedOptionKey === key;
                        const carrierLabel =
                          option.carrierDescription || option.carrier || "";
                        const carrierKey = String(option.carrier || "")
                          .trim()
                          .toLowerCase()
                          .replace(/\s+/g, "");
                        const modeLabel =
                          option.bookingMode === "pickup_plus_label"
                            ? t(
                                "coreReturn.modePickup",
                                "Pickup + prepaid label"
                              )
                            : t(
                                "coreReturn.modeDropoff",
                                "Print label and drop off"
                              );
                        const eta =
                          option.deliveryDays != null
                            ? t("coreReturn.deliveryDays", "{days} day estimate")
                                .replace(
                                  "{days}",
                                  String(option.deliveryDays)
                                )
                            : option.deliveryDate
                              ? String(option.deliveryDate)
                              : "";
                        const dropOffLocations = (
                          Array.isArray(option.dropOffLocations)
                            ? option.dropOffLocations
                            : []
                        ).filter((location) => {
                          const locationCarrier = String(location?.carrier || "")
                            .trim()
                            .toLowerCase()
                            .replace(/\s+/g, "");
                          return (
                            !locationCarrier ||
                            !carrierKey ||
                            locationCarrier === carrierKey
                          );
                        });
                        return (
                          <Box key={key}>
                            <ChoiceCard
                              selected={selected}
                              disabled={saving}
                              onClick={() => setSelectedOptionKey(key)}
                              title={`${carrierLabel} · ${
                                option.serviceDescription || option.service
                              }`}
                              subtitle={[modeLabel, eta]
                                .filter(Boolean)
                                .join(" · ")}
                            />
                            {selected ? (
                              <Box sx={{ mt: 1, mb: 0.5, pl: 0.5 }}>
                                <MDTypography
                                  variant="overline"
                                  sx={{
                                    opacity: 0.7,
                                    letterSpacing: "0.08em",
                                  }}
                                >
                                  {t(
                                    "coreReturn.dropOffTitleForCarrier",
                                    "{carrier} drop-off locations"
                                  ).replace(
                                    "{carrier}",
                                    carrierLabel ||
                                      t(
                                        "coreReturn.dropOffTitle",
                                        "Nearby drop-off locations"
                                      )
                                  )}
                                </MDTypography>
                                {dropOffLocations.length ? (
                                  <Box
                                    sx={{ mt: 1, display: "grid", gap: 1 }}
                                  >
                                    {dropOffLocations
                                      .slice(0, 8)
                                      .map((location) => (
                                        <Box
                                          key={
                                            location.id ||
                                            location.code ||
                                            location.addressText ||
                                            location.name
                                          }
                                          sx={{
                                            p: 1.25,
                                            borderRadius: `${p.radiusSm}px`,
                                            border: `1px solid ${p.lineStrong}`,
                                            bgcolor: "rgba(15,15,18,0.02)",
                                          }}
                                        >
                                          <MDTypography
                                            variant="button"
                                            fontWeight={800}
                                          >
                                            {location.name}
                                          </MDTypography>
                                          <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{ color: p.muted, mt: 0.25 }}
                                          >
                                            {location.addressText ||
                                              [location.street, location.city]
                                                .filter(Boolean)
                                                .join(", ")}
                                          </MDTypography>
                                          {location.mapsUrl ? (
                                            <PortalButton
                                              variant="text"
                                              size="sm"
                                              onClick={() =>
                                                window.open(
                                                  location.mapsUrl,
                                                  "_blank",
                                                  "noopener,noreferrer"
                                                )
                                              }
                                            >
                                              {t(
                                                "coreReturn.openMap",
                                                "Open map"
                                              )}
                                            </PortalButton>
                                          ) : null}
                                        </Box>
                                      ))}
                                  </Box>
                                ) : (
                                  <PortalAlert severity="info" sx={{ mt: 1 }}>
                                    {t(
                                      "coreReturn.dropOffEmpty",
                                      "No nearby drop-off locations were returned for this carrier. Use the carrier’s website locator after printing your label."
                                    )}
                                  </PortalAlert>
                                )}
                              </Box>
                            ) : null}
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <PortalAlert severity="warning" sx={{ mb: 2 }}>
                      {t(
                        "coreReturn.noOptions",
                        "No prepaid drop-off options are available right now."
                      )}
                    </PortalAlert>
                  )}

                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <PortalButton
                      variant="ghost"
                      startIcon="arrow_back"
                      disabled={saving}
                      component={Link}
                      to="/account/core-returns"
                    >
                      {t("common.back", "Back")}
                    </PortalButton>
                    <PortalButton
                      variant="ghost"
                      disabled={saving || optionsLoading}
                      onClick={loadShippingOptions}
                    >
                      {t("coreReturn.refreshOptions", "Refresh options")}
                    </PortalButton>
                    <PortalButton
                      variant="primary"
                      disabled={
                        saving ||
                        optionsLoading ||
                        !selectedOption ||
                        !canSubmitCore
                      }
                      onClick={onPrintLabel}
                    >
                      {saving
                        ? t("coreReturn.generatingLabel", "Generating label...")
                        : t("coreReturn.printLabel", "Print label")}
                    </PortalButton>
                  </Box>
                </>
              )}

              {activeRequest ? (
                <Box sx={{ mt: 3 }}>
                  <MDTypography
                    variant="overline"
                    sx={{ opacity: 0.7, letterSpacing: "0.08em" }}
                  >
                    {t("coreReturn.timelineTitle", "Return progress")}
                  </MDTypography>
                  <Box sx={{ mt: 1.25, display: "grid", gap: 0.75 }}>
                    {workflowSteps.map((row) => (
                      <Box
                        key={row.key}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Icon
                          sx={{
                            fontSize: 18,
                            color: row.done || row.current
                              ? row.current
                                ? p.accent
                                : p.success || "#147A5C"
                              : p.muted,
                          }}
                        >
                          {row.done
                            ? "check_circle"
                            : row.current
                              ? "radio_button_checked"
                              : "radio_button_unchecked"}
                        </Icon>
                        <MDTypography
                          variant="button"
                          fontWeight={row.done || row.current ? 800 : 600}
                          sx={{
                            opacity: row.done || row.current ? 1 : 0.7,
                            color: row.current ? p.accent : undefined,
                          }}
                        >
                          {row.label}
                        </MDTypography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : null}
            </PortalCard>
          ) : null}
        </>
      )}
    </CustomerPortalLayout>
  );
}
